import * as React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { tokens } from 'styles/tokens';

export interface ITileProps {
    children: React.ReactNode;
    navigateTo?: string;
    isExternalLink?: boolean;
    className?: string;
    'aria-label'?: string;
}

export const TileStyles = css`
    border: ${tokens.card.border};
    box-shadow: ${tokens.card.shadow};
    border-radius: 8px;
    color: ${tokens.text.primary};
    background-color: ${tokens.card.background};
    transition: all 0.15s ease-in-out;

    &:hover {
        box-shadow: ${tokens.card.shadowHover};
        border: ${tokens.card.borderHover};
    }

    &:active {
        transition: none;
        border: ${tokens.card.borderActive};
    }
`;

const TileContainer = styled.div`
    ${TileStyles}
`;

export function Tile({
    children,
    navigateTo,
    isExternalLink,
    className,
    'aria-label': ariaLabel,
}: ITileProps) {
    const innerContent = (
        <TileContainer
            data-cy="tile-container"
            className={`py-13 px-8 ${navigateTo ? 'cursor-pointer' : ''} tile-container ${
                className ?? ''
            }`}
        >
            <div
                className="flex flex-row flex-grow-1 align-center full-height"
                data-cy="tile-content"
            >
                {children}
            </div>
        </TileContainer>
    );

    if (!navigateTo) {
        return (
            <div aria-label={ariaLabel} className="no-text-decoration">
                {innerContent}
            </div>
        );
    }

    if (isExternalLink) {
        return (
            <a
                aria-label={ariaLabel}
                className="no-text-decoration"
                target="_blank"
                rel="noopener noreferrer"
                href={navigateTo}
            >
                {innerContent}
            </a>
        );
    }

    return (
        <Link
            aria-label={ariaLabel}
            className="no-text-decoration"
            to={navigateTo || '#'}
        >
            {innerContent}
        </Link>
    );
}
